import { useState } from "react";

// Storage-agnostic state hook backed by a Web Storage area (sessionStorage by default).
//
// RW-60: contact personal data must live only in sessionStorage — cleared when the tab closes,
// surviving an in-tab refresh — and NEVER in localStorage (which persists indefinitely and is a
// data-at-rest / shared-device / XSS exposure). The storage area is injectable for tests; the app
// always uses sessionStorage. See ADR-RW-60-0001 and docs/specs/RW-60-stop-localstorage-contact-pii.md.
const useSessionStorage = <T>(
  key: string,
  initialValue: T,
  getStorage: () => Storage = () => window.sessionStorage,
) => {
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === "undefined") {
      return initialValue;
    }
    try {
      const item = getStorage().getItem(key);
      return item ? (JSON.parse(item) as T) : initialValue;
    } catch (error) {
      // Stored value may be a crafted/corrupt JSON string: fall back to the initial value.
      // Log only the error — never the stored value, which can contain personal data.
      console.error(error);
      return initialValue;
    }
  });

  const setValue = (value: T | ((val: T) => T)) => {
    try {
      const valueToStore =
        value instanceof Function ? value(storedValue) : value;
      // Update in-memory state first so the in-session journey works even if the write below
      // fails (e.g. Safari private mode / quota).
      setStoredValue(valueToStore);
      if (typeof window !== "undefined") {
        getStorage().setItem(key, JSON.stringify(valueToStore));
      }
    } catch (error) {
      // Swallow storage write failures; log only the error, never the value (no PII in logs).
      console.error(error);
    }
  };

  return [storedValue, setValue] as const;
};

export default useSessionStorage;
